"use client";

/* eslint-disable react/jsx-props-no-spreading */

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import AirDoctorAssistantContent from "@/components/molecules/confirmation-section/air-doctor-assistant-content";
import BtaVotingContent from "@/components/molecules/confirmation-section/bta-voting-content";
import EmergencyPhoneNumberContent from "@/components/molecules/confirmation-section/emergency-phone-number-content";
import GhicPromotion from "@/components/molecules/confirmation-section/ghic-promotion";
import MakeClaimContent from "@/components/molecules/confirmation-section/make-claim-content";
import PolicyRenewalContent from "@/components/molecules/confirmation-section/policy-renewal-content";
import ReferFriendContent from "@/components/molecules/confirmation-section/refer-friend-content";
import TrustPilotContent from "@/components/molecules/confirmation-section/trust-pilot-content";
import withBrand from "@/components/with-brand";
import { usePhoneNumbers } from "@/utils/phone-number-exp.utils";
import { CombinedConfirmation } from "@/utils/schema/confirmation-page-schema.utils";

import AboutCover from "./about-cover";
import Styles from "./confirmation-section.module.css";

function ConfirmationSection({
  brand,
  ...props
}: Readonly<CombinedConfirmation>) {
  const newPhoneNumbersList = usePhoneNumbers() ?? [];

  const phoneNumbersMap = newPhoneNumbersList.reduce(
    (acc, phoneNumber) => {
      acc[phoneNumber.name] = phoneNumber;
      return acc;
    },
    {} as Record<string, (typeof newPhoneNumbersList)[number]>
  );

  const { current } = useWizard();

  return (
    <main
      className=" p-spacing-l md:px-spacing-xxl md:py-spacing-xl lg:px-spacing-6xl lg:py-spacing-xxl flex flex-col gap-spacing-m md:gap-spacing-xl"
      data-testid="confirmation-section"
    >
      {/* LOGIN SECTION & IMAGE BANNER */}
      <div>
        <AboutCover {...props.aboutCoverArea["0"]} />
      </div>

      <div className="grid lg:grid-cols-12 ">
        <div className="grid lg:grid-cols-12 lg:col-span-12 lg:order-3 gap-spacing-m md:gap-spacing-xl lg:gap-spacing-l h-fit">
          <div className=" grid col-span-12 lg:col-span-7 gap-spacing-m md:gap-spacing-xl lg:gap-spacing-l">
            {/* POLICY RENEWAL */}
            <div className="bg-dark-surface-accent-1-primary text-dark-text-title rounded-radius-xxl p-spacing-m  md:p-spacing-l lg:p-spacing-xl space-y-spacing-s md:space-y-spacing-m h-fit ">
              <PolicyRenewalContent {...props.yourPolicyDetailsArea["0"]} />
            </div>
            {/* BTA VOTING CONTENT */}
            {props.btaVotingArea?.["0"] && (
              <BtaVotingContent variant="1" {...props.btaVotingArea["0"]} />
            )}
            {/* REFER A FRIEND CONTENT */}
            {current.uiElementControls?.showReferFriendSection && (
              <div
                className={`${brand} ${Styles.referFriendBg} rounded-radius-xxl md:relative md:overflow-hidden hidden lg:block`}
              >
                <ReferFriendContent {...props.referFriendArea["0"]} />
              </div>
            )}
          </div>

          <div className="grid lg:row-span-3 col-span-12 lg:col-span-5 gap-spacing-m md:gap-spacing-xl lg:gap-spacing-l">
            <div>
              <div className="grid lg:grid-rows-span-3 gap-spacing-m md:gap-spacing-xl lg:gap-spacing-l">
                {/* EMERGENCY PHONE NUMBER */}
                <div
                  className={`bg-surface-neutral-option-1 rounded-radius-xxl p-spacing-m  md:p-spacing-l lg:p-spacing-xl space-y-spacing-s md:space-y-spacing-m h-fit `}
                >
                  <EmergencyPhoneNumberContent
                    callingFromTheUK={phoneNumbersMap.callingFromTheUK?.default}
                    callingFromOutsideTheUK={
                      phoneNumbersMap.callingFromOutsideTheUK?.default
                    }
                    callingFromMexico={
                      phoneNumbersMap.callingFromMexico?.default
                    }
                    callingFromUSAorCanada={
                      phoneNumbersMap.callingFromUSAorCanada?.default
                    }
                    {...props.emergencyContactArea["0"]}
                  />
                </div>

                {/**Air Doctor Implementation */}
                {current.uiElementControls?.showAirDoctorAssistantCard && (
                  <div
                    className={`bg-surface-neutral-option-1 rounded-radius-xxl p-spacing-m  md:p-spacing-l lg:p-spacing-xl space-y-spacing-s md:space-y-spacing-m h-fit `}
                  >
                    <AirDoctorAssistantContent
                      {...props.airDoctorAssistantArea["0"]}
                    />
                  </div>
                )}

                {/* MAKE A CLAIM */}
                <div
                  className={` bg-surface-neutral-option-1 rounded-radius-xxl p-spacing-m  md:p-spacing-l lg:p-spacing-xl space-y-spacing-s md:space-y-spacing-m h-fit`}
                >
                  <MakeClaimContent
                    {...props.makingCliamsArea["0"]}
                    medicalAssistanceInEurope={
                      phoneNumbersMap.medicalAssistanceInEurope?.default
                    }
                    nonEmergencyClaims={
                      phoneNumbersMap.nonEmergencyClaims?.default
                    }
                    medicalAssistanceWithinTheUSAandCanada={
                      phoneNumbersMap.medicalAssistanceWithinTheUSAandCanada
                        ?.default
                    }
                  />
                </div>

                {/* PROMOTING GHIC Desktop Only */}
                {props.nhsArea["0"] && current.uiElementControls?.showNHS && (
                  <div
                    className={` bg-surface-promo hidden lg:block rounded-radius-xxl p-spacing-l  md:p-spacing-xl space-y-spacing-s md:space-y-spacing-m h-fit`}
                  >
                    <GhicPromotion {...props.nhsArea["0"]} />
                  </div>
                )}
                {/* TRUST PILOT CONTENT Desktop Only */}
                <div className="hidden lg:block">
                  <TrustPilotContent {...props.trustPilotArea["0"]} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:hidden grid gap-spacing-m md:gap-spacing-xl lg:gap-spacing-l md:mt-spacing-xl sm:mt-spacing-m">
          {/* REFER A FRIEND CONTENT */}
          {current.uiElementControls?.showReferFriendSection && (
            <div
              className={`${brand} ${Styles.referFriendBg}  rounded-radius-xxl md:relative md:overflow-hidden md:h-full  lg:col-span-7 lg:order-4 lg:hidden block`}
            >
              <ReferFriendContent {...props.referFriendArea["0"]} />
            </div>
          )}

          {/* PROMOTING GHIC Tab and Mobile */}
          {props.nhsArea["0"] && current.uiElementControls?.showNHS && (
            <div
              className={` bg-surface-promo lg:hidden rounded-radius-xxl px-spacing-m py-spacing-l md:p-spacing-xl space-y-spacing-s md:space-y-spacing-m h-fit`}
            >
              <GhicPromotion {...props.nhsArea["0"]} />
            </div>
          )}
          {/* TRUST PILOT CONTENT Tab and Mobile */}
          <div className="lg:hidden">
            <TrustPilotContent {...props.trustPilotArea["0"]} />
          </div>
        </div>
      </div>
    </main>
  );
}

export default withBrand(ConfirmationSection);
